import { useState } from "react";
import { TokenCard } from "@allaria-tech/matter/TokenCard";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  authCoownerToken,
  authSignatoryToken,
  authToken,
  regenerateToken,
} from "../../connectors/connectors";
import { updateToken } from "../../reducers/authReducer";
import { authorizeDraft, getDraftUUIDFromToken } from "../../app/utils";
import { onUpdateDraftUUID } from "../../reducers/onboardingDataReducer";
import { getPartnerCompanyType, minutesToMiliseconds } from "../../utils";
import { getCompanyData } from "../../utils/companies";

// Mask email 3 last characters after the @
const maskEmail = (email?: string) => {
  if (!email) return "";
  const [user, domain] = email.split("@");
  return `${user.slice(0, -3)}***@${domain}`;
};

interface EmailTokenCardProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthorize: (draftUUID?: string) => Promise<void>;
  onSuccess: () => void | Promise<void>;
}

// Manual-code entry for email validation. Renders Matter's TokenCard modal
// (self-contained: it auto-authorizes on a complete token and owns its resend
// button/countdown), wiring the same authorize/resend/success logic the step
// used before.
const EmailTokenCard = ({
  isOpen,
  onClose,
  onAuthorize,
  onSuccess,
}: EmailTokenCardProps) => {
  const dispatch = useAppDispatch();
  const { signatureRequestId } = useAppSelector(
    (state) => state.authentication,
  );
  const { onboardingType } = useAppSelector((state) => state.activeTemplate);
  const onboardingData = useAppSelector((state) => state.onboardingData);
  const { formValues } = onboardingData;

  const partnerCompanyType = formValues && getPartnerCompanyType(formValues);

  const [lastTokenSent, setLastTokenSent] = useState<number>(Date.now());

  const sendToken = async ({
    signatureRequestId,
    token,
  }: {
    signatureRequestId: number;
    token: string;
  }) => {
    switch (onboardingType) {
      case "COOWNER":
        return await authToken({
          email: formValues?.email,
          signatureRequestId,
          token,
        });
      case "ADDITIONAL_COOWNER":
        return await authCoownerToken({
          email: formValues?.email,
          signatureRequestId,
          token,
          externalCode: onboardingData.draftUUID,
        });
      case "SIGNATORY":
        return await authSignatoryToken({
          email: formValues?.email,
          signatureRequestId,
          token,
          externalCode: onboardingData.draftUUID,
        });
      default:
        return await authToken({
          email: formValues?.email,
          signatureRequestId,
          token,
        });
    }
  };

  return (
    <TokenCard
      isOpen={isOpen}
      onClose={onClose}
      title="Verificar con código"
      autoSendToken
      hideResendButton
      lastSendToken={lastTokenSent + minutesToMiliseconds(2)}
      onAuthorize={async (token) => {
        try {
          if (!signatureRequestId)
            throw new Error("Error: signatureRequestId not found");

          const authResponse = await sendToken({
            signatureRequestId,
            token,
          });

          dispatch(updateToken(authResponse.data.token));

          const draftUUID = getDraftUUIDFromToken({
            token: authResponse.data.token,
          });

          dispatch(onUpdateDraftUUID(draftUUID));

          await authorizeDraft(
            async () => {
              await onAuthorize?.(draftUUID);
            },
            async () => {
              await onAuthorize?.(draftUUID);
              await onSuccess();
            },
          );

          return {
            success: true,
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          if (error.response?.data?.code === "INVALID_TOKEN") {
            return {
              success: false,
              message:
                "El token ingresado es incorrecto. Verificalo e intentá nuevamente.",
            };
          } else if (error.response?.data?.code === "EXPIRED_TOKEN") {
            return {
              success: false,
              message:
                "El token ha expirado. Solicitá uno nuevo para continuar.",
            };
          } else if (error.response?.data?.code === "MAX_ATTEMPTS_REACHED") {
            return {
              success: false,
              message:
                "Superaste el número máximo de intentos permitidos. Por seguridad, generá un nuevo token.",
            };
          } else
            return {
              success: false,
              message: "Error al autorizar el token.",
            };
        }
      }}
      onResend={async () => {
        if (!signatureRequestId || !formValues?.email) return;

        try {
          await regenerateToken({
            email: formValues?.email,
            signatureRequestId,
            companyType: getCompanyData().companyName,
            ...(partnerCompanyType ? { partnerCompanyType } : {}),
            es: true,
          });

          setLastTokenSent(Date.now());
        } catch (error) {
          console.error(error);
        }
      }}
      onSuccess={() => {
        void onSuccess();
      }}
    >
      Ingresá el código que se encuentra en el email que enviamos a {maskEmail(formValues?.email)}. El enlace es válido durante 24 horas
    </TokenCard>
  );
};

export default EmailTokenCard;
